import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { TableSortState } from "components/table";
import { useAppTranslation } from "locale/useAppTranslation";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import {
  useDriverServiceV1GetDriversQuery,
  useDriverServiceV1BulkDeleteDriverMutation,
} from "_services/modules/transport-resource/driver";
import useDebounce from "hooks/shared/useDebounce";
import { useDriverColumns } from "./useDriverColumns";
import { DriverDraftRow } from "../types";
import { dateTimeFormatter } from "_helper/timeFormatter";
const getVendorCode = (vendorName: string) => {
  if (!vendorName || vendorName === "-" || vendorName === "Unknown Vendor") {
    return "-";
  }
  let hash = 0;
  for (let i = 0; i < vendorName.length; i++) {
    hash = vendorName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const codeNum = Math.abs(hash % 90000) + 10000;
  return `VND - ${codeNum}`;
};
export const useDriverList = () => {
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation("driver");
  // ======================
  // STATE
  // ======================
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortState, setSortState] = useState<TableSortState>(null);
  // ======================
  // BULK ACTION STATE
  // ======================
  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [stagedBulkAction, setStagedBulkAction] = useState<string | null>(null);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  // ======================
  // DELETE MODAL STATE
  // ======================
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<DriverDraftRow | null>(
    null
  );
  // ======================
  // STATUS MODAL STATE
  // ======================
  const [statusModal, setStatusModal] = useState<{
    show: boolean;
    type: "success" | "error" | "warning";
    title: string;
    message: React.ReactNode;
  }>({
    show: false,
    type: "success",
    title: "",
    message: "",
  });
  // ======================
  // EFFECTS
  // ======================
  useEffect(() => {
    if (selectedKeys.length === 0) {
      setStagedBulkAction(null);
    }
  }, [selectedKeys]);
  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-draft") || "Driver Drafts",
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);
  // ======================
  // SORT MAPPING
  // ======================
  const sortByMap: Record<string, string> = {
    driverName: "full_name",
    vendor: "vendor_name",
    phone: "phone_number",
    licenseType: "license_type",
  };
  // ======================
  // API
  // ======================
  const {
    data: apiResponse,
    isLoading,
    isFetching,
    refetch,
  } = useDriverServiceV1GetDriversQuery({
    page: currentPage,
    limit: rowsPerPage,
    search: debouncedSearch || undefined,
    status: "Draft",
    sortBy: sortState ? sortByMap[sortState.columnId] : "created_at",
    order: sortState?.direction ?? "desc",
  } as any);
  const [deleteDrivers, { isLoading: isDeleting }] =
    useDriverServiceV1BulkDeleteDriverMutation();
  // ======================
  // PAGINATION
  // ======================
  const totalPages = apiResponse?.metadata?.total_page ?? 1;
  const totalData = apiResponse?.metadata?.total ?? 0;
  // ======================
  // TABLE DATA
  // ======================
  const tableData: DriverDraftRow[] = useMemo(() => {
    if (!apiResponse?.driver) {
      return [];
    }
    return apiResponse.driver.map((item: any) => ({
      id: item.id || "",
      driverName: item.full_name || "",
      driverPicture: item.photo_url || "",
      vendor: item.vendor_name || "-",
      vendorId: getVendorCode(item.vendor_name),
      phone: item.phone_number || "-",
      status: item.status || "Draft",
      licenseType: item.license_type || "-",
      licenseNumber: item.license_number || "-",
      createdBy: item.created_by?.name || "-",
      createdAt: item.created_by?.timestamp
        ? dateTimeFormatter(item.created_by.timestamp)
        : "-",
      updatedBy: item.updated_by?.name || "-",
      updatedAt: item.updated_by?.timestamp
        ? dateTimeFormatter(item.updated_by.timestamp)
        : "-",
    }));
  }, [apiResponse]);
  // ======================
  // DELETE HANDLER
  // ======================
  const handleDeleteClick = (row: DriverDraftRow) => {
    setDriverToDelete(row);
    setIsDeleteOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!driverToDelete) {
      return;
    }
    try {
      await deleteDrivers({
        ids: [driverToDelete.id],
      }).unwrap();
      setIsDeleteOpen(false);
      setStatusModal({
        show: true,
        type: "success",
        title: t("modal-success-discard-title" as any) || "Draft Discarded",
        message: t("modal-success-discard-message" as any, {
          name: driverToDelete.driverName,
        }),
      });
      setDriverToDelete(null);
      refetch();
    } catch {
      setIsDeleteOpen(false);
      toast.error("Failed to discard driver draft");
      setDriverToDelete(null);
    }
  };
  // ======================
  // BULK DELETE
  // ======================
  const handleConfirmBulkDelete = async () => {
    if (selectedKeys.length === 0) {
      return;
    }
    const count = selectedKeys.length;
    try {
      await deleteDrivers({
        ids: selectedKeys.map(String),
      }).unwrap();
      setShowBulkConfirmModal(false);
      setStatusModal({
        show: true,
        type: "success",
        title:
          t("modal-success-bulk-discard-title" as any) || "Drafts Discarded",
        message: t("modal-success-bulk-discard-message" as any, {
          count,
        }),
      });
      setSelectedKeys([]);
      setStagedBulkAction(null);
      refetch();
    } catch {
      setShowBulkConfirmModal(false);
      toast.error("Failed to discard selected driver drafts");
    }
  };
  // ======================
  // COLUMNS
  // ======================
  const columns = useDriverColumns({
    t,
    handleDeleteRequest: handleDeleteClick,
  });
  // ======================
  // RETURN
  // ======================
  return {
    columns,
    tableData,
    search,
    setSearch,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    sortState,
    setSortState,
    isDeleteOpen,
    setIsDeleteOpen,
    driverToDelete,
    setDriverToDelete,
    isLoading,
    isFetching,
    totalPages,
    totalData,
    isDeleting,
    handleDeleteClick,
    handleConfirmDelete,
    t,
    // Bulk action
    selectedKeys,
    setSelectedKeys,
    isBulkOpen,
    setIsBulkOpen,
    stagedBulkAction,
    setStagedBulkAction,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleConfirmBulkDelete,
    // Status modal
    statusModal,
    setStatusModal,
  };
};
